import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { ArrowLeft, Shield, RefreshCw, Download, CheckCircle2, ToggleLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

export default function ConsentLog() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ popia_accepted_total: 0, info_sharing_updated_total: 0 });
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [emailFilter, setEmailFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('all');

  const fetchLog = useCallback(async () => {
    if (user?.role !== 'platform_owner') return;
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = new URLSearchParams({ skip: String(skip), limit: String(PAGE_SIZE) });
      if (emailFilter.trim()) params.append('email', emailFilter.trim());
      if (eventFilter !== 'all') params.append('event', eventFilter);
      const r = await fetch(`${API_URL}/api/consent/admin/log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not load consent log');
      }
      const d = await r.json();
      setEntries(d.entries || []);
      setTotal(d.total || 0);
      setSummary(d.summary || { popia_accepted_total: 0, info_sharing_updated_total: 0 });
    } catch (err) {
      toast.error(err?.message || 'Failed to load consent log');
    } finally {
      setLoading(false);
    }
  }, [user?.role, skip, emailFilter, eventFilter]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSearch = (e) => { e?.preventDefault?.(); setSkip(0); fetchLog(); };

  const exportCsv = () => {
    if (entries.length === 0) { toast.info('Nothing to export on this page'); return; }
    const header = ['logged_at', 'event', 'user_id', 'email', 'platform', 'policy_version', 'terms_version', 'accepted', 'ip'];
    const rows = entries.map((r) => [
      r.logged_at,
      r.event,
      r.user_id,
      r.email,
      r.details?.platform,
      r.details?.policy_version,
      r.details?.terms_version,
      r.details?.accepted === undefined ? '' : String(r.details.accepted),
      r.details?.ip,
    ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consent-log-page-${Math.floor(skip / PAGE_SIZE) + 1}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const fmtDetails = (e) => {
    if (e.event === 'popia_accepted') {
      return `Policy v${e.details?.policy_version || '?'} / Terms v${e.details?.terms_version || '?'} / ${e.details?.platform || '?'}`;
    }
    if (e.event === 'info_sharing_updated') {
      return e.details?.accepted ? 'Enabled' : 'Revoked';
    }
    return JSON.stringify(e.details || {});
  };

  if (user?.role !== 'platform_owner') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Access denied</CardTitle>
            <CardDescription>This page is restricted to BodyCorp365 super admins.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const page = Math.floor(skip / PAGE_SIZE) + 1;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/platform" className="text-slate-500 hover:text-slate-700" data-testid="back-to-platform">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Shield className="w-5 h-5 text-sky-600" />
          <div className="flex-1">
            <h1 className="font-manrope font-semibold text-slate-900">Consent Log</h1>
            <p className="text-xs text-slate-500">Append-only POPIA &amp; information-sharing audit trail</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLog} data-testid="refresh-log-btn">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              <div>
                <div className="text-2xl font-semibold text-slate-900" data-testid="summary-popia">{summary.popia_accepted_total}</div>
                <div className="text-xs text-slate-500">POPIA acceptances</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <ToggleLeft className="w-8 h-8 text-sky-500" />
              <div>
                <div className="text-2xl font-semibold text-slate-900" data-testid="summary-sharing">{summary.info_sharing_updated_total}</div>
                <div className="text-xs text-slate-500">Info-sharing changes</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <Shield className="w-8 h-8 text-violet-500" />
              <div>
                <div className="text-2xl font-semibold text-slate-900" data-testid="summary-total">{total}</div>
                <div className="text-xs text-slate-500">Entries (current filter)</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="text-xs text-slate-500 mb-1 block">Filter by email</label>
                <Input
                  value={emailFilter}
                  onChange={(e) => setEmailFilter(e.target.value)}
                  placeholder="e.g. meryl@... (substring, case-insensitive)"
                  data-testid="filter-email"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Event</label>
                <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setSkip(0); }}>
                  <SelectTrigger data-testid="filter-event"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All events</SelectItem>
                    <SelectItem value="popia_accepted">POPIA accepted</SelectItem>
                    <SelectItem value="info_sharing_updated">Info-sharing changed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="apply-filters-btn">Apply</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <CardContent className="p-4 space-y-2">
              {[1,2,3,4,5].map((i) => <Skeleton key={i} className="h-8 w-full" />)}
            </CardContent>
          ) : entries.length === 0 ? (
            <CardContent className="p-12 text-center" data-testid="empty-log">
              <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-900 mb-1">No entries</h3>
              <p className="text-sm text-slate-500">No consent events match the current filters.</p>
            </CardContent>
          ) : (
            <Table data-testid="consent-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((e, idx) => (
                  <TableRow key={`${e.user_id}-${e.logged_at}-${idx}`} data-testid={`log-row-${idx}`}>
                    <TableCell className="text-sm text-slate-700 whitespace-nowrap">{fmtDate(e.logged_at)}</TableCell>
                    <TableCell>
                      {e.event === 'popia_accepted' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">POPIA</Badge>
                      ) : (
                        <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100">Sharing</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium text-slate-900">{e.email || '—'}</div>
                      <div className="text-xs text-slate-400">{e.user_id}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDetails(e)}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{e.details?.ip || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-slate-500" data-testid="page-info">
              Page {page} of {lastPage} — {total} entries total
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={skip === 0}
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                data-testid="prev-page-btn"
              >Previous</Button>
              <Button
                variant="outline"
                size="sm"
                disabled={skip + PAGE_SIZE >= total}
                onClick={() => setSkip(skip + PAGE_SIZE)}
                data-testid="next-page-btn"
              >Next</Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
